import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  COUNT_UP_ANIMATION_DURATION,
  ETransactionType,
  ICON_SIZES,
} from "../../helpers/constants";
import {
  formatCurrency,
  getSelectionTotalsByTransactionType,
  rgbaObjectToString,
} from "../../helpers/utils";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import clsx from "clsx";
import RadioGroup from "../RadioGroup/RadioGroup";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import { MdChecklist } from "react-icons/md";
import InfoBlock from "../InfoBlock/InfoBlock";
import useCurrentUser from "../../hooks/useCurrentUser";
import CheckBox from "../CheckBox/CheckBox";
import useCollapsibles from "../../hooks/useCollapsibles";
import useLocalStorageState from "use-local-storage-state";
import SlideDownUpAnimation from "../Animations/SlideDownUpAnimation";
import CountUp from "react-countup";
import { useTranslation } from "react-i18next";

interface ISelectionTotals {
  transactions: Array<any>;
  isSummarized?: boolean;
}

const SELECTION_TOTALS_KEY = "selection-totals";
const SUM = "sum";
const DIFF = "diff";
const DEFAULTS = {
  isSum: true,
  sumIncome: true,
  sumExpenses: true,
};

interface ISelectionTotalsBlock {
  color?: string;
  currencySymbol: string;
  lineThrough?: boolean;
  label: string;
  total: number;
}

const SelectionTotalsBlock: React.FC<ISelectionTotalsBlock> = ({
  color = "",
  currencySymbol,
  lineThrough = false,
  label,
  total,
}) => {
  const hasDecimals = useCallback((value: any) => {
    return value % 1 !== 0;
  }, []);

  return (
    <div
      className={clsx([
        "flex",
        "flex-row",
        "gap-2",
        "items-center",
        "justify-between",
        "w-full",
      ])}
    >
      <div
        className={clsx([
          "text-sm",
          "font-medium",
          { "line-through": lineThrough },
        ])}
      >
        {label}
      </div>
      <div
        className={clsx([
          "text-xl",
          "text-right",
          "font-medium",
          { "line-through": lineThrough },
        ])}
        style={{
          color,
        }}
      >
        <CountUp
          end={total}
          duration={COUNT_UP_ANIMATION_DURATION}
          separator=","
          decimals={hasDecimals(total) ? 2 : 0}
          delay={0.1}
          preserveValue
          prefix={currencySymbol}
        />
      </div>
    </div>
  );
};

const SelectionTotals: React.FC<ISelectionTotals> = ({
  transactions,
  isSummarized,
}) => {
  const { t } = useTranslation();
  const { colors, userInfo } = useCurrentUser();
  const [selectionTotalsPreferences, setSelectionTotalsPreferences] =
    useLocalStorageState(`${userInfo?._id}-${SELECTION_TOTALS_KEY}`, {
      defaultValue: DEFAULTS,
    });

  const [isSum, setIsSum] = useState<boolean>(selectionTotalsPreferences.isSum);
  const [sumIncome, setSumIncome] = useState<boolean>(
    selectionTotalsPreferences.sumIncome
  );
  const [sumExpenses, setSumExpenses] = useState<boolean>(
    selectionTotalsPreferences.sumExpenses
  );
  const { currencySymbol } = useCurrentAccount();
  const { collapsiblePreferences, updateCollapsible } = useCollapsibles();

  const incomeTotal = useMemo(
    () =>
      getSelectionTotalsByTransactionType({
        transactions,
        type: ETransactionType.INCOME,
      }),
    [transactions]
  );

  const expensesTotal = useMemo(
    () =>
      getSelectionTotalsByTransactionType({
        transactions,
        type: ETransactionType.EXPENSE,
      }),
    [transactions]
  );

  const total = useMemo(() => {
    if (isSum) {
      let result = 0;
      if (sumIncome) result += incomeTotal;
      if (sumExpenses) result += expensesTotal;

      return result;
    }

    return incomeTotal - expensesTotal;
  }, [incomeTotal, expensesTotal, isSum, sumExpenses, sumIncome]);

  const onChange = (value: string | number) => {
    setIsSum(value === SUM);
  };

  useEffect(() => {
    setSelectionTotalsPreferences({ sumExpenses, sumIncome, isSum });
  }, [sumExpenses, sumIncome, isSum]);

  return (
    <CollapsibleBlock
      title={t("selection_calculations.title")}
      uppercase
      open={collapsiblePreferences.selectionOpen}
      badge={formatCurrency({ value: total, currency: currencySymbol })}
      onChange={(isOpen: boolean) =>
        updateCollapsible({ selectionOpen: isOpen })
      }
    >
      <div className={clsx(["gap-4", "flex", "flex-col", "w-full"])}>
        {isSummarized ? (
          <SlideDownUpAnimation show={transactions?.length === 0}>
            <InfoBlock
              displayMobileIcon
              message={t("selection_calculations.select_one")}
              icon={<MdChecklist size={ICON_SIZES.MD_LG} />}
            />
          </SlideDownUpAnimation>
        ) : (
          <SlideDownUpAnimation show={transactions?.length <= 1}>
            <InfoBlock
              displayMobileIcon
              message={t("selection_calculations.select_two")}
              icon={<MdChecklist size={ICON_SIZES.MD_LG} />}
            />
          </SlideDownUpAnimation>
        )}
        <div className={clsx(["flex", "flex-col", "gap-2"])}>
          <div
            className={clsx([
              "flex",
              "flex-col",
              "gap-2",
              "md:text-base",
              "text-sm",
            ])}
          >
            {t("selection_calculations.calculation_type_description")}
          </div>
          <RadioGroup
            layout="vertical"
            options={[
              {
                label: t("selection_calculations.sum"),
                description: t("selection_calculations.sum_description"),
                value: SUM,
              },
              {
                label: t("selection_calculations.diff"),
                description: t("selection_calculations.diff_description"),
                value: DIFF,
              },
            ]}
            onChange={(value: any) => {
              onChange(value);
            }}
            selected={isSum ? SUM : DIFF}
          />
        </div>

        <SlideDownUpAnimation show={isSum}>
          <div className={clsx(["flex", "flex-col", "gap-2"])}>
            <div className={clsx(["md:text-base", "text-sm"])}>
              {t("selection_calculations.transaction_type_description")}
            </div>
            <div className={clsx(["flex", "flex-row", "gap-4"])}>
              <CheckBox
                onChange={setSumIncome}
                checked={sumIncome}
                label={t("selection_calculations.income")}
              />
              <CheckBox
                onChange={setSumExpenses}
                checked={sumExpenses}
                label={t("selection_calculations.expenses")}
              />
            </div>
          </div>
        </SlideDownUpAnimation>

        <div className={clsx(["flex", "flex-col", "gap-2"])}>
          <SlideDownUpAnimation show={!!(sumIncome || !isSum)}>
            <SelectionTotalsBlock
              label={
                !sumExpenses && isSum
                  ? t("selection_calculations.total")
                  : t("selection_calculations.income")
              }
              lineThrough={isSum && !sumIncome}
              color={rgbaObjectToString(colors.transactions.income.bg)}
              total={incomeTotal}
              currencySymbol={currencySymbol}
            />
          </SlideDownUpAnimation>

          <SlideDownUpAnimation show={!!(sumExpenses || !isSum)}>
            <SelectionTotalsBlock
              label={
                !sumIncome && isSum
                  ? t("selection_calculations.total")
                  : t("selection_calculations.expenses")
              }
              lineThrough={isSum && !sumExpenses}
              color={rgbaObjectToString(colors.transactions.expenses.bg)}
              total={expensesTotal}
              currencySymbol={currencySymbol}
            />
          </SlideDownUpAnimation>

          <SlideDownUpAnimation show={!!((sumIncome && sumExpenses) || !isSum)}>
            <SelectionTotalsBlock
              label={t("selection_calculations.total")}
              total={total}
              currencySymbol={currencySymbol}
            />
          </SlideDownUpAnimation>
        </div>
      </div>
    </CollapsibleBlock>
  );
};

export default React.memo(SelectionTotals);
